const numberValue = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const activeClaim = (claim) =>
  claim?.include_in_plan !== false && ["active", "transferred"].includes(claim?.status || "active");

export const CLAIM_LIABILITY_KEYS = [
  "debtor1",
  "debtor2",
  "joint",
  "debtor1_third",
  "debtor2_third",
  "third",
  "unknown",
];

export const claimUnsecuredAmount = (claim) => {
  if (claim?.unsecured_amount !== null && claim?.unsecured_amount !== undefined && claim?.unsecured_amount !== "") {
    return Math.max(0, numberValue(claim.unsecured_amount));
  }
  return Math.max(
    0,
    numberValue(claim?.amount_claimed)
      - numberValue(claim?.secured_amount)
      - numberValue(claim?.priority_amount)
      - numberValue(claim?.administrative_amount),
  );
};

export function sortCreditorsByClaimAmount(creditors, claimAmount) {
  const source = Array.isArray(creditors) ? creditors : [];
  const parsedClaimAmount = Number(claimAmount);
  const hasClaimAmount = claimAmount !== null
    && claimAmount !== undefined
    && claimAmount !== ""
    && Number.isFinite(parsedClaimAmount);

  return source
    .map((creditor, originalIndex) => ({ creditor, originalIndex }))
    .sort((left, right) => {
      const leftBalance = Number(left.creditor?.balance);
      const rightBalance = Number(right.creditor?.balance);
      const leftHasBalance = left.creditor?.balance !== null
        && left.creditor?.balance !== undefined
        && left.creditor?.balance !== ""
        && Number.isFinite(leftBalance);
      const rightHasBalance = right.creditor?.balance !== null
        && right.creditor?.balance !== undefined
        && right.creditor?.balance !== ""
        && Number.isFinite(rightBalance);

      if (hasClaimAmount) {
        if (leftHasBalance !== rightHasBalance) return leftHasBalance ? -1 : 1;
        if (leftHasBalance && rightHasBalance) {
          const difference = Math.abs(leftBalance - parsedClaimAmount)
            - Math.abs(rightBalance - parsedClaimAmount);
          if (difference !== 0) return difference;
        }
      }

      const nameDifference = String(left.creditor?.account_name || "").localeCompare(
        String(right.creditor?.account_name || ""),
        undefined,
        { sensitivity: "base" },
      );
      return nameDifference || left.originalIndex - right.originalIndex;
    })
    .map(({ creditor }) => creditor);
}

export function proofOfClaimsReportFilename(caseData, debtorOne) {
  const rawCaseNumber = String(caseData?.case_number || caseData?.case_id || "case").trim();
  const withoutDivision = rawCaseNumber.includes(":")
    ? rawCaseNumber.split(":", 2)[1]
    : rawCaseNumber;
  const cleanedCaseNumber = withoutDivision.replace("-bk-", "-");
  const caseParts = cleanedCaseNumber.split("-");
  const shortCaseNumber = caseParts.length >= 2
    ? caseParts.slice(0, 2).join("-")
    : cleanedCaseNumber;
  const lastName = String(debtorOne?.last_name || "Debtor")
    .replace(/[\\/"\r\n]+/g, " ")
    .replace(/\s+/g, " ")
    .trim() || "Debtor";
  return `${shortCaseNumber} ${lastName} claims liability report.pdf`;
}

const normalizedCreditorValue = (value) => String(value || "")
  .trim()
  .toLowerCase()
  .replace(/[_-]+/g, " ");

export function scheduledClaimDefaults(creditor) {
  if (!creditor) return {};
  const debtType = normalizedCreditorValue(creditor.debt_type);
  const accountType = normalizedCreditorValue(creditor.account_type);
  const lienNature = normalizedCreditorValue(creditor.nature_of_lien);
  const priorityType = normalizedCreditorValue(creditor.priority_type);
  const claimClassification = {
    secured: "secured",
    priority: "priority",
    unsecured: "unsecured",
    executory: "executory",
  }[debtType] || "unsecured";

  let claimType = "other";
  if (debtType === "secured" && (lienNature === "judgment" || accountType === "judgment lien")) {
    claimType = "judgment_lien";
  } else if (debtType === "secured" && (lienNature === "statutory" || accountType === "statutory lien")) {
    claimType = "statutory_lien";
  } else if (debtType === "priority" && [priorityType, accountType].some((value) => ["dso", "child support", "domestic support obligation"].includes(value))) {
    claimType = "dso";
  } else if (debtType === "priority" && [priorityType, accountType].includes("tax")) {
    claimType = "tax";
  } else if (debtType === "executory") {
    claimType = "lease";
  } else {
    claimType = {
      mortgage: "mortgage",
      "home loan": "mortgage",
      "real estate": "mortgage",
      auto: "vehicle",
      automobile: "vehicle",
      car: "vehicle",
      vehicle: "vehicle",
      "vehicle loan": "vehicle",
      "student loan": "student_loan",
      "education loan": "student_loan",
      "household goods": "household_goods",
      furniture: "household_goods",
      lease: "lease",
      tax: "tax",
      dso: "dso",
      "child support": "dso",
    }[accountType] || "other";
  }

  return {
    claim_classification: claimClassification,
    claim_type: claimType,
  };
}

const claimAccountType = (claim, fallback = "") => {
  const types = {
    student_loan: "student loan",
    judgment_lien: "judgment lien",
    statutory_lien: "statutory lien",
    household_goods: "other",
  };
  return types[claim?.claim_type] || (claim?.claim_type !== "other" ? claim?.claim_type : fallback) || "other";
};

const claimRows = (claim, scheduledById) => {
  const matchedId = claim?.matched_creditor ? String(claim.matched_creditor) : "";
  const scheduled = scheduledById.get(matchedId) || {};
  const total = Math.max(0, numberValue(claim?.amount_claimed));
  const classification = claim?.claim_classification || "unsecured";
  const secured = Math.max(
    0,
    numberValue(claim?.secured_amount) || (classification === "secured" ? total : 0),
  );
  const priority = Math.max(
    0,
    numberValue(claim?.priority_amount) || (classification === "priority" ? total : 0),
  );
  const administrative = Math.max(
    0,
    numberValue(claim?.administrative_amount) || (classification === "administrative" ? total : 0),
  );
  let unsecured = claimUnsecuredAmount(claim);
  if (classification === "unsecured" && unsecured === 0) unsecured = total;

  const base = {
    ...scheduled,
    account_name: claim?.creditor_name || scheduled.account_name || `Claim #${claim?.claim_number}`,
    full_address: claim?.creditor_address || scheduled.full_address || "",
    who_owes: claim?.liability === "unknown" ? (scheduled.who_owes || "unknown") : claim?.liability,
    account_type: claimAccountType(claim, scheduled.account_type),
    arrearage: claim?.arrearage ?? scheduled.arrearage ?? null,
    asset_links: claim?.asset_links?.length ? claim.asset_links : (scheduled.asset_links || []),
    collateral_description: claim?.collateral_description || scheduled.collateral_description || "",
    claim_source: true,
    claim_id: claim?.id,
    claim_number: claim?.claim_number,
    matched_creditor_id: matchedId || null,
    scheduled_balance: scheduled.balance ?? null,
    source_creditor_id: scheduled.id || null,
  };
  const makeRow = (part, debtType, balance, extra = {}) => ({
    ...base,
    ...extra,
    id: `claim-${claim?.id}-${part}`,
    debt_type: debtType,
    balance,
    claim_part: part,
  });

  if (classification === "executory") {
    return [makeRow("executory", "executory", total, {
      description_of_lease: claim?.collateral_description || scheduled.description_of_lease || "Lease / executory contract",
    })];
  }

  const rows = [];
  if (secured > 0) rows.push(makeRow("secured", "secured", secured));
  if (priority > 0) rows.push(makeRow("priority", "priority", priority, { priority_amount: priority }));
  if (administrative > 0) {
    rows.push(makeRow("administrative", "priority", administrative, {
      priority_amount: administrative,
      account_type: "administrative",
    }));
  }
  if (unsecured > 0) rows.push(makeRow("unsecured", "unsecured", unsecured));
  if (!rows.length && total > 0) rows.push(makeRow("unsecured", "unsecured", total));
  return rows;
};

export function buildPlanCreditors(scheduledCreditors, claims, source = "scheduled") {
  const scheduled = Array.isArray(scheduledCreditors) ? scheduledCreditors : [];
  if (source === "scheduled") return scheduled;

  const includedClaims = (Array.isArray(claims) ? claims : []).filter(activeClaim);
  const scheduledById = new Map(scheduled.map((creditor) => [String(creditor.id), creditor]));
  const filedRows = includedClaims.flatMap((claim) => claimRows(claim, scheduledById));
  if (source === "claims") return filedRows;

  const replacedScheduledIds = new Set(
    includedClaims
      .map((claim) => claim?.matched_creditor)
      .filter(Boolean)
      .map(String),
  );
  return [
    ...scheduled.filter((creditor) => !replacedScheduledIds.has(String(creditor.id))),
    ...filedRows,
  ];
}

export function claimReconciliationSummary(claims) {
  const included = (Array.isArray(claims) ? claims : []).filter(activeClaim);
  return {
    includedCount: included.length,
    unmatchedCount: included.filter((claim) => !claim?.matched_creditor).length,
    warningCount: included.reduce(
      (count, claim) => count + (Array.isArray(claim?.reconciliation_warnings) ? claim.reconciliation_warnings.length : 0),
      0,
    ),
    total: included.reduce((sum, claim) => sum + numberValue(claim?.amount_claimed), 0),
  };
}

export function claimLiabilitySummary(claims) {
  const groups = Object.fromEntries(CLAIM_LIABILITY_KEYS.map((key) => [key, {
    key,
    claimCount: 0,
    amount: 0,
    securedAmount: 0,
    priorityAmount: 0,
    unsecuredAmount: 0,
    unmatchedCount: 0,
  }]));

  (Array.isArray(claims) ? claims : []).filter(activeClaim).forEach((claim) => {
    const key = groups[claim?.liability] ? claim.liability : "unknown";
    const amount = numberValue(claim?.amount_claimed);
    groups[key].claimCount += 1;
    groups[key].amount += amount;
    groups[key].securedAmount += numberValue(claim?.secured_amount);
    groups[key].priorityAmount += numberValue(claim?.priority_amount);
    groups[key].unsecuredAmount += claimUnsecuredAmount(claim);
    if (!claim?.matched_creditor) {
      groups[key].unmatchedCount += 1;
    }
  });

  return CLAIM_LIABILITY_KEYS.map((key) => groups[key]);
}
